import { RubyLoader } from '../components/RubyLoader';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ChevronLeft, Receipt, Download, ChevronRight, Search,
  CreditCard, CheckCircle2, XCircle, Clock, RotateCcw, Filter, X,
} from 'lucide-react';
import { api } from '../services/apiService';
import { DatePicker } from '../components/DatePicker';
import { MultiSelectDropdown } from '../components/MultiSelectDropdown';

/**
 * Transaction Reporting — list page.
 *
 * URL-backed state (bookmarkable):
 *   tenantId, from, to, search
 *   status / brand / locationId / terminalId — comma-separated for multi
 *
 * Data: the backend (/tenants/{id}/payments) returns items + registerOptions
 * + locationOptions in one round-trip, so the filter dropdowns can show
 * friendly names the moment the page loads. When the tenant has no
 * registers the dropdowns stay empty and the date/status/brand filters
 * still work.
 */

interface PaymentRow {
  transactionId: string;
  paymentId: string;
  createdAt: string;
  status: string;
  method: string;
  gatewayProvider?: string;
  amount: number;
  amounts?: { subtotal: number; tip: number; surcharge: number; total: number };
  cardBrand?: string;
  cardLast4?: string;
  last4?: string;
  approvalCode?: string;
  gatewayTransactionId?: string;
  employeeId?: string;
  registerId?: string;
  registerName?: string;
  locationId?: string;
  locationName?: string;
}

interface ApiRegisterOption {
  terminalId: string;
  name?: string;
  locationId?: string;
  locationName?: string;
}

interface ApiLocationOption {
  locationId: string;
  locationName?: string;
}

const STATUS_META: Record<string, { label: string; tint: string; Icon: React.FC<any> }> = {
  completed: { label: 'Approved', tint: 'bg-emerald-50 text-emerald-700 border-emerald-200', Icon: CheckCircle2 },
  approved: { label: 'Approved', tint: 'bg-emerald-50 text-emerald-700 border-emerald-200', Icon: CheckCircle2 },
  settled: { label: 'Settled', tint: 'bg-emerald-50 text-emerald-700 border-emerald-200', Icon: CheckCircle2 },
  declined: { label: 'Declined', tint: 'bg-red-50 text-red-700 border-red-200', Icon: XCircle },
  cancelled: { label: 'Cancelled', tint: 'bg-red-50 text-red-700 border-red-200', Icon: XCircle },
  error: { label: 'Error', tint: 'bg-red-50 text-red-700 border-red-200', Icon: XCircle },
  voided: { label: 'Voided', tint: 'bg-amber-50 text-amber-700 border-amber-200', Icon: RotateCcw },
  refunded: { label: 'Refunded', tint: 'bg-stone-100 text-stone-700 border-stone-200', Icon: RotateCcw },
  partially_refunded: { label: 'Partial Refund', tint: 'bg-stone-100 text-stone-700 border-stone-200', Icon: RotateCcw },
  sent_to_poi: { label: 'Awaiting Card', tint: 'bg-blue-50 text-blue-700 border-blue-200', Icon: Clock },
  pending: { label: 'Pending', tint: 'bg-stone-100 text-stone-600 border-stone-200', Icon: Clock },
  timeout: { label: 'Timed Out', tint: 'bg-orange-50 text-orange-700 border-orange-200', Icon: Clock },
  orphaned: { label: 'Orphaned', tint: 'bg-orange-50 text-orange-700 border-orange-200', Icon: Clock },
  disputed: { label: 'Chargeback', tint: 'bg-red-50 text-red-700 border-red-200', Icon: XCircle },
};

const BRAND_OPTIONS = [
  { value: 'visa', label: 'Visa' },
  { value: 'mastercard', label: 'Mastercard' },
  { value: 'amex', label: 'American Express' },
  { value: 'discover', label: 'Discover' },
  { value: 'diners', label: "Diner's Club" },
];

const STATUS_OPTIONS = [
  { value: 'completed', label: 'Approved' },
  { value: 'declined', label: 'Declined' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'voided', label: 'Voided' },
  { value: 'refunded', label: 'Refunded' },
  { value: 'sent_to_poi', label: 'Awaiting Card' },
  { value: 'timeout', label: 'Timed Out' },
  { value: 'disputed', label: 'Chargeback' },
];

const todayIso = () => new Date().toISOString().slice(0, 10);

// Parse comma-separated URL param into an array of strings.
const parseCsv = (v: string | null): string[] =>
  v ? v.split(',').map(s => s.trim()).filter(Boolean) : [];

const PaymentsListPage: React.FC = () => {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();

  const urlTenantId = params.get('tenantId') || '';
  const tenantInStorage = sessionStorage.getItem('activeTenantId') ||
    localStorage.getItem('activeTenantId') || '';
  const tenantId = urlTenantId || tenantInStorage;

  const statuses = parseCsv(params.get('status'));
  const brands = parseCsv(params.get('brand'));
  const locationIds = parseCsv(params.get('locationId'));
  const terminalIds = parseCsv(params.get('terminalId'));
  const from = params.get('from') || todayIso();
  const to = params.get('to') || todayIso();
  const search = params.get('search') || '';

  useEffect(() => {
    if (urlTenantId) sessionStorage.setItem('activeTenantId', urlTenantId);
  }, [urlTenantId]);

  const setFilter = useCallback((updates: Record<string, string | string[] | null>) => {
    const next = new URLSearchParams(params);
    for (const [key, value] of Object.entries(updates)) {
      if (value === null || value === '' || (Array.isArray(value) && value.length === 0)) {
        next.delete(key);
      } else if (Array.isArray(value)) {
        next.set(key, value.join(','));
      } else {
        next.set(key, value);
      }
    }
    setParams(next, { replace: true });
  }, [params, setParams]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [items, setItems] = useState<PaymentRow[]>([]);
  const [registerOptions, setRegisterOptions] = useState<ApiRegisterOption[]>([]);
  const [locationOptions, setLocationOptions] = useState<ApiLocationOption[]>([]);
  const [searchInput, setSearchInput] = useState(search);

  // Debounce the free-text search so typing doesn't refetch on every keystroke
  useEffect(() => {
    const t = setTimeout(() => {
      if (searchInput !== search) setFilter({ search: searchInput || null });
    }, 350);
    return () => clearTimeout(t);
  }, [searchInput, search, setFilter]);

  const fetchList = useCallback(async () => {
    if (!tenantId) { setLoading(false); return; }
    try {
      setLoading(true);
      setError(null);
      // Backend takes single-valued filters (status/brand/terminalId/locationId)
      // — multi-select is applied client-side so the dropdowns can combine
      // freely. We still pass `search` through since the backend substring
      // match is cheaper than shipping the full table.
      const resp = await api.posPayments.list({ search });
      setItems(resp?.items || []);
      setRegisterOptions(resp?.registerOptions || []);
      setLocationOptions(resp?.locationOptions || []);
    } catch (err: any) {
      setError(err?.message || 'Failed to load transactions');
    } finally {
      setLoading(false);
    }
  }, [tenantId, search]);

  useEffect(() => { fetchList(); }, [fetchList]);

  // Client-side filtering for everything except search
  const filtered = useMemo(() => {
    const fromMs = from ? new Date(from + 'T00:00:00').getTime() : -Infinity;
    const toMs = to ? new Date(to + 'T23:59:59.999').getTime() : Infinity;
    const statusSet = new Set(statuses);
    const brandSet = new Set(brands.map(b => b.toLowerCase()));
    const locSet = new Set(locationIds);
    const termSet = new Set(terminalIds);
    return items.filter(p => {
      const t = p.createdAt ? new Date(p.createdAt).getTime() : 0;
      if (t < fromMs || t > toMs) return false;
      if (statusSet.size > 0 && !statusSet.has(p.status)) return false;
      if (brandSet.size > 0 && !brandSet.has((p.cardBrand || '').toLowerCase())) return false;
      if (locSet.size > 0 && (!p.locationId || !locSet.has(p.locationId))) return false;
      if (termSet.size > 0 && (!p.registerId || !termSet.has(p.registerId))) return false;
      return true;
    });
  }, [items, from, to, statuses, brands, locationIds, terminalIds]);

  const kpis = useMemo(() => {
    const base = { count: 0, approved: 0, declined: 0, volume: 0, refunded: 0 };
    for (const p of filtered) {
      base.count++;
      const amt = Number(p.amounts?.total || p.amount || 0);
      if (p.status === 'completed' || p.status === 'approved' || p.status === 'settled') {
        base.approved++;
        base.volume += amt;
      } else if (p.status === 'declined' || p.status === 'cancelled' || p.status === 'error') {
        base.declined++;
      } else if (p.status === 'refunded' || p.status === 'partially_refunded') {
        base.refunded++;
      }
    }
    return base;
  }, [filtered]);

  // Location/register multi-select options — filtered by selected locations
  const locationMultiOptions = useMemo(() =>
    locationOptions.map(l => ({
      value: l.locationId,
      label: l.locationName || l.locationId,
      subtitle: l.locationName && l.locationName !== l.locationId ? l.locationId : undefined,
    })),
    [locationOptions]);

  const registerMultiOptions = useMemo(() => {
    const locFilter = new Set(locationIds);
    return registerOptions
      .filter(r => locFilter.size === 0 || (r.locationId && locFilter.has(r.locationId)))
      .map(r => ({
        value: r.terminalId,
        label: r.name || r.terminalId,
        subtitle: [r.locationName, r.terminalId].filter(Boolean).join(' · '),
      }));
  }, [registerOptions, locationIds]);

  // Scope breadcrumb
  const scopeLabel = (() => {
    if (terminalIds.length === 1) {
      const r = registerOptions.find(x => x.terminalId === terminalIds[0]);
      if (r) return `${r.name || r.terminalId}${r.locationName ? ` · ${r.locationName}` : ''}`;
      return terminalIds[0];
    }
    if (terminalIds.length > 1) return `${terminalIds.length} terminals`;
    if (locationIds.length === 1) {
      const loc = locationOptions.find(x => x.locationId === locationIds[0]);
      return loc?.locationName || locationIds[0];
    }
    if (locationIds.length > 1) return `${locationIds.length} locations`;
    return 'All terminals';
  })();

  const exportCsv = useCallback(() => {
    const header = ['Date', 'Status', 'Brand', 'Last4', 'Amount', 'Tip', 'Surcharge', 'Approval', 'NMI Txn', 'Register', 'Location', 'Payment ID'];
    const rows = filtered.map(p => {
      const a = p.amounts || { subtotal: 0, tip: 0, surcharge: 0, total: p.amount };
      return [
        p.createdAt,
        STATUS_META[p.status]?.label || p.status,
        (p.cardBrand || '').toUpperCase(),
        p.cardLast4 || p.last4 || '',
        Number(a.total || p.amount).toFixed(2),
        Number(a.tip || 0).toFixed(2),
        Number(a.surcharge || 0).toFixed(2),
        p.approvalCode || '',
        p.gatewayTransactionId || '',
        p.registerName || p.registerId || '',
        p.locationName || p.locationId || '',
        p.transactionId,
      ];
    });
    const csv = [header, ...rows].map(r => r.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `transactions-${from}-to-${to}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  }, [filtered, from, to]);

  const hasActiveFilters = statuses.length > 0 || brands.length > 0 ||
    locationIds.length > 0 || terminalIds.length > 0 || !!search ||
    from !== todayIso() || to !== todayIso();

  const clearFilters = () => {
    const next = new URLSearchParams();
    if (urlTenantId) next.set('tenantId', urlTenantId);
    setParams(next, { replace: true });
    setSearchInput('');
  };

  const presetToday = () => setFilter({ from: todayIso(), to: todayIso() });
  const presetLast7 = () => {
    const d = new Date(); d.setDate(d.getDate() - 6);
    setFilter({ from: d.toISOString().slice(0, 10), to: todayIso() });
  };
  const presetLast30 = () => {
    const d = new Date(); d.setDate(d.getDate() - 29);
    setFilter({ from: d.toISOString().slice(0, 10), to: todayIso() });
  };
  const presetThisMonth = () => {
    const d = new Date();
    const first = new Date(d.getFullYear(), d.getMonth(), 1);
    setFilter({ from: first.toISOString().slice(0, 10), to: todayIso() });
  };
  const dateLabel = (() => {
    if (from === to && from === todayIso()) return 'Today';
    if (from === to) return 'Custom';
    return 'Custom range';
  })();

  return (
    <div className="min-h-screen bg-stone-100 -m-3 md:-m-6">
      <div className="bg-black px-4 py-4 md:py-5">
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-1 text-gray-400 hover:text-white text-sm mb-3 transition-colors"
        >
          <ChevronLeft size={16} /> Back
        </button>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="p-2.5 rounded-xl bg-gray-800 border border-gray-700 shrink-0">
            <Receipt size={18} className="text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="text-white text-base md:text-lg font-semibold">Transactions</h1>
            <p className="text-gray-400 text-xs md:text-sm mt-0.5 truncate">{scopeLabel}</p>
          </div>
          <button
            onClick={exportCsv}
            disabled={filtered.length === 0}
            className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-sm font-semibold bg-white text-black hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Download size={14} /> Export CSV
          </button>
        </div>
      </div>

      <div className="p-4 md:p-6 max-w-6xl mx-auto space-y-4">
        {!tenantId && (
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 text-sm text-amber-800">
            <p className="font-semibold mb-1">No tenant selected</p>
            <p>Open this page from a tenant context, or add <code className="bg-amber-100 px-1 rounded">?tenantId=&lt;id&gt;</code> to the URL.</p>
          </div>
        )}

        {tenantId && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Kpi label="Transactions" value={String(kpis.count)} />
              <Kpi label="Approved" value={String(kpis.approved)} tone="emerald" />
              <Kpi label="Declined" value={String(kpis.declined)} tone="red" />
              <Kpi label="Net Volume" value={`$${kpis.volume.toFixed(2)}`} tone="bold" />
            </div>

            <div className="bg-white rounded-2xl shadow-sm border border-stone-200/60 p-4 space-y-3">
              <div className="flex items-center gap-2 text-stone-600 flex-wrap">
                <Filter size={14} />
                <span className="text-sm font-semibold">Filters</span>
                <div className="flex gap-1.5 ml-2">
                  <PresetChip label="Today" active={from === to && from === todayIso()} onClick={presetToday} />
                  <PresetChip label="7d" active={false} onClick={presetLast7} />
                  <PresetChip label="30d" active={false} onClick={presetLast30} />
                  <PresetChip label="MTD" active={false} onClick={presetThisMonth} />
                  <PresetChip label={dateLabel} active={from !== to || from !== todayIso()} fancy />
                </div>
                {hasActiveFilters && (
                  <button
                    onClick={clearFilters}
                    className="ml-auto text-xs text-stone-500 hover:text-stone-700 inline-flex items-center gap-1"
                  >
                    <X size={12} /> Clear all
                  </button>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                <FilterField label="From">
                  <DatePicker value={from} onChange={v => setFilter({ from: v })} max={to} />
                </FilterField>

                <FilterField label="To">
                  <DatePicker value={to} onChange={v => setFilter({ to: v })} min={from} />
                </FilterField>

                <FilterField label="Locations">
                  <MultiSelectDropdown
                    options={locationMultiOptions}
                    value={locationIds}
                    onChange={v => setFilter({ locationId: v, terminalId: [] })}
                    placeholder="All locations"
                    searchPlaceholder="Search locations..."
                  />
                </FilterField>

                <FilterField label="Terminals">
                  <MultiSelectDropdown
                    options={registerMultiOptions}
                    value={terminalIds}
                    onChange={v => setFilter({ terminalId: v })}
                    placeholder="All terminals"
                    searchPlaceholder="Search terminals..."
                  />
                </FilterField>

                <FilterField label="Statuses">
                  <MultiSelectDropdown
                    options={STATUS_OPTIONS}
                    value={statuses}
                    onChange={v => setFilter({ status: v })}
                    placeholder="All statuses"
                  />
                </FilterField>

                <FilterField label="Card brands">
                  <MultiSelectDropdown
                    options={BRAND_OPTIONS}
                    value={brands}
                    onChange={v => setFilter({ brand: v })}
                    placeholder="All brands"
                  />
                </FilterField>

                <div className="md:col-span-2">
                  <FilterField label="Search">
                    <div className="relative">
                      <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400 pointer-events-none" />
                      <input
                        type="text"
                        value={searchInput}
                        onChange={e => setSearchInput(e.target.value)}
                        placeholder="Approval code, last 4 digits, txn id, order id…"
                        className="w-full pl-9 pr-3 h-10 rounded-lg border border-stone-300 bg-white text-sm text-stone-800 placeholder:text-stone-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                      />
                    </div>
                  </FilterField>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-sm border border-stone-200/60 overflow-hidden">
              {loading ? (
                <RubyLoader variant="section" />
              ) : error ? (
                <div className="p-8 text-center text-sm text-red-600">{error}</div>
              ) : filtered.length === 0 ? (
                <div className="p-12 text-center">
                  <Receipt size={32} className="mx-auto mb-3 text-stone-300" />
                  <p className="text-stone-800 font-semibold mb-1">No transactions</p>
                  <p className="text-sm text-stone-500">
                    {hasActiveFilters
                      ? 'Nothing matches these filters. Try widening the date range or clearing them.'
                      : 'No card transactions yet for this scope.'}
                  </p>
                </div>
              ) : (
                <div className="divide-y divide-stone-100">
                  {filtered.map(p => (
                    <TransactionRow
                      key={p.transactionId}
                      p={p}
                      onClick={() => navigate(`/payments/${encodeURIComponent(p.transactionId)}?tenantId=${encodeURIComponent(tenantId)}`)}
                    />
                  ))}
                </div>
              )}
            </div>
          </>
        )}

        <div className="h-4" />
      </div>
    </div>
  );
};

const TransactionRow: React.FC<{ p: PaymentRow; onClick: () => void }> = ({ p, onClick }) => {
  const status = STATUS_META[p.status] || { label: p.status, tint: 'bg-stone-100 text-stone-700 border-stone-200', Icon: Clock };
  const amount = Number(p.amounts?.total || p.amount || 0);
  const tip = Number(p.amounts?.tip || 0);
  const last4 = p.cardLast4 || p.last4 || '';
  return (
    <button
      onClick={onClick}
      className="w-full px-4 md:px-5 py-3 flex items-center gap-3 hover:bg-stone-50 text-left transition-colors"
    >
      <div className="shrink-0 flex items-center gap-2.5 w-40">
        {p.cardBrand ? (
          <div className={`w-9 h-6 rounded-md ${cardBrandBg(p.cardBrand)} text-white text-[9px] font-bold uppercase flex items-center justify-center tracking-wider`}>
            {cardBrandShort(p.cardBrand)}
          </div>
        ) : (
          <div className="w-9 h-6 rounded-md bg-stone-200 text-stone-500 flex items-center justify-center">
            <CreditCard size={12} />
          </div>
        )}
        <span className="text-sm font-mono text-stone-700">
          {last4 ? `••${last4}` : '—'}
        </span>
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold border ${status.tint}`}>
            <status.Icon size={10} />
            {status.label}
          </span>
          {p.approvalCode && (
            <span className="text-[11px] font-mono text-stone-500 truncate">auth {p.approvalCode}</span>
          )}
        </div>
        <p className="text-xs text-stone-500 mt-1 truncate">
          {formatDate(p.createdAt)}
          {p.registerName && <> · <span className="text-stone-700 font-medium">{p.registerName}</span></>}
          {p.locationName && <> · {p.locationName}</>}
        </p>
      </div>

      <div className="text-right shrink-0">
        <p className="text-sm font-bold text-stone-800 tabular-nums">${amount.toFixed(2)}</p>
        {tip > 0 && <p className="text-[11px] text-emerald-700 tabular-nums">+${tip.toFixed(2)} tip</p>}
      </div>

      <ChevronRight size={14} className="text-stone-300 shrink-0 ml-1" />
    </button>
  );
};

// ---------------------------------------------------------------------------
// Primitives
// ---------------------------------------------------------------------------

const Kpi: React.FC<{ label: string; value: string; tone?: 'emerald' | 'red' | 'bold' }> = ({ label, value, tone }) => (
  <div className="bg-white rounded-2xl border border-stone-200/60 p-4">
    <p className="text-[11px] uppercase tracking-wide text-stone-500">{label}</p>
    <p className={`mt-1 text-2xl font-bold ${
      tone === 'emerald' ? 'text-emerald-700' :
      tone === 'red' ? 'text-red-700' :
      tone === 'bold' ? 'text-stone-900' : 'text-stone-800'
    }`}>
      {value}
    </p>
  </div>
);

const FilterField: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div>
    <label className="block text-[11px] uppercase tracking-wide text-stone-500 mb-1.5 font-medium">{label}</label>
    {children}
  </div>
);

const PresetChip: React.FC<{ label: string; active: boolean; onClick?: () => void; fancy?: boolean }> = ({ label, active, onClick, fancy }) => (
  <button
    onClick={onClick}
    disabled={!onClick && !fancy}
    className={`px-2.5 py-1 rounded-md text-[11px] font-semibold transition-colors ${
      active
        ? 'bg-emerald-100 text-emerald-700 border border-emerald-200'
        : fancy
        ? 'bg-stone-100 text-stone-500 border border-stone-200 cursor-default'
        : 'bg-stone-100 text-stone-600 border border-stone-200 hover:bg-stone-200'
    }`}
  >
    {label}
  </button>
);

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function cardBrandBg(brand: string): string {
  const b = (brand || '').toLowerCase();
  if (b === 'visa') return 'bg-gradient-to-br from-blue-600 to-blue-800';
  if (b === 'mastercard' || b === 'mc') return 'bg-gradient-to-br from-orange-500 to-red-600';
  if (b === 'amex' || b.includes('american')) return 'bg-gradient-to-br from-sky-600 to-blue-800';
  if (b === 'discover') return 'bg-gradient-to-br from-orange-400 to-orange-600';
  return 'bg-gradient-to-br from-stone-600 to-stone-800';
}

function cardBrandShort(brand: string): string {
  const b = (brand || '').toLowerCase();
  if (b === 'visa') return 'Visa';
  if (b === 'mastercard' || b === 'mc') return 'MC';
  if (b === 'amex' || b.includes('american')) return 'Amex';
  if (b === 'discover') return 'Disc';
  if (b === 'diners') return 'Dc';
  return brand?.slice(0, 4) || 'Card';
}

function formatDate(iso: string): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-US', {
      month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true,
    });
  } catch { return iso; }
}

export default PaymentsListPage;
